import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { IdleWarningModal } from './IdleWarningModal';
import {
  DEFAULT_IDLE_TIMEOUT_MS,
  DEFAULT_IDLE_WARNING_MS,
} from '../constants/idleTimeouts.js';

/**
 * Context for pages that need something other than the default idle window.
 *
 * Two ways out of the default, and they are not interchangeable:
 *
 * - `setTimeoutDuration(id, ms)` / `resetTimeoutDuration(id)` — BOU-2974. A page that
 *   needs longer than 10 minutes (image/video generation, journey authoring, a live
 *   fight) stretches the window instead of removing it. The guard still fires, so an
 *   abandoned tab still lets go of its socket and the Cloud Run instance can spin
 *   down. Call these through `useLongRunningIdleTimeout`, which supplies the id.
 * - `keepAlive()` — the companion to the above. The window is measured from the last
 *   DOM activity, and an unattended run produces none, so a job that outlives the
 *   long tier (Scene Flight's generateAll is ~2.25h at 9 legs) would be evicted
 *   mid-run while its loop kept billing into an unmounted tree. Calling this on each
 *   job event makes the hour count from the last sign of life instead of the last
 *   click — still finite, just measuring the right thing.
 * - `disableGlobalTimeout()` / `enableGlobalTimeout()` — BOU-2510. Only for a page
 *   that runs `useIdleTimeout` ITSELF and does something gentler on expiry (pause and
 *   offer a reconnect, rather than redirect). Two timers otherwise race and the
 *   wrapper's redirect wins. Suppressing without owning a replacement timer is the
 *   bug BOU-2974 fixed — it leaves the surface with no guard at all, forever.
 */
const IdleTimeoutContext = createContext({
  disableGlobalTimeout: () => {},
  enableGlobalTimeout: () => {},
  // No-ops when a surface renders outside any wrapper (some dev-tree routes, tests
  // that mount a component bare) — asking for a longer window is simply ignored
  // rather than crashing.
  setTimeoutDuration: () => {},
  resetTimeoutDuration: () => {},
  keepAlive: () => {},
});

export function useIdleTimeoutControl() {
  return useContext(IdleTimeoutContext);
}

/**
 * Wrapper component that enforces idle timeout on protected pages.
 * When the user is idle for too long, redirects them to the home page.
 *
 * The redirect is the whole point (BOU-2974): the user stays SIGNED IN — this is
 * `navigate('/')`, not an Auth0 logout — but unmounting the page closes every
 * WebSocket, SSE stream and poller it held, which is what lets the Cloud Run backend
 * scale to zero. A surface with no timeout keeps an instance warm indefinitely.
 *
 * The window is DEFAULT_IDLE_TIMEOUT_MS unless a mounted surface has asked for
 * longer, in which case it is the longest window anyone currently mounted asked for.
 * Surfaces ask through context rather than by prop because the production tree
 * applies ONE wrapper for the whole ProtectedRoute Outlet group — a per-route prop
 * could never reach it.
 *
 * Requests are keyed per surface, and that is load-bearing rather than tidiness: two
 * long-running surfaces are mounted together whenever Scene Flight is open as an
 * Image Lab tab. With a single shared slot, closing the tab would hand the whole
 * still-open Image Lab back to the 10-minute window mid-generation.
 *
 * Usage:
 *   <IdleTimeoutWrapper>
 *     <SomePage />
 *   </IdleTimeoutWrapper>
 */
export function IdleTimeoutWrapper({ children }) {
  const navigate = useNavigate();
  const [globalEnabled, setGlobalEnabled] = useState(true);
  // Every currently-mounted surface that wants a longer window, by its own id.
  const [durationRequests, setDurationRequests] = useState(() => new Map());

  const disableGlobalTimeout = useCallback(() => {
    setGlobalEnabled(false);
  }, []);

  const enableGlobalTimeout = useCallback(() => {
    setGlobalEnabled(true);
  }, []);

  const setTimeoutDuration = useCallback((id, ms) => {
    setDurationRequests((prev) => {
      if (prev.get(id) === ms) return prev;  // no-op re-render guard
      const next = new Map(prev);
      next.set(id, ms);
      return next;
    });
  }, []);

  const resetTimeoutDuration = useCallback((id) => {
    setDurationRequests((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // The longest ask wins: a surface that needs an hour keeps it for as long as it is
  // mounted, no matter what else mounts or unmounts around it.
  const effectiveTimeoutMs = durationRequests.size > 0
    ? Math.max(...durationRequests.values())
    : DEFAULT_IDLE_TIMEOUT_MS;

  const {
    isWarning,
    isTimedOut,
    warningSecondsLeft,
    acknowledgeWarning,
    voluntaryDisconnect,
    resetTimer,
  } = useIdleTimeout({
    timeoutMs: effectiveTimeoutMs,
    warningDurationMs: DEFAULT_IDLE_WARNING_MS,
    enabled: globalEnabled,
  });

  // Held in a ref so `keepAlive` keeps a stable identity: consumers call it from poll
  // loops and effect bodies, and a changing identity would restart those.
  const resetTimerRef = useRef(resetTimer);
  resetTimerRef.current = resetTimer;

  // 'explicit', because job progress is a real sign of life and should count even
  // while the warning modal is counting down — the same reason a click does.
  const keepAlive = useCallback(() => {
    resetTimerRef.current?.('explicit');
  }, []);

  // When timed out, redirect to home
  React.useEffect(() => {
    if (isTimedOut && globalEnabled) {
      console.log('⏰ [IDLE] Page timed out - redirecting to home');
      navigate('/');
    }
  }, [isTimedOut, globalEnabled, navigate]);

  return (
    <IdleTimeoutContext.Provider value={{
      disableGlobalTimeout,
      enableGlobalTimeout,
      setTimeoutDuration,
      resetTimeoutDuration,
      keepAlive,
    }}>
      {children}
      {globalEnabled && (
        <IdleWarningModal
          open={isWarning}
          secondsLeft={warningSecondsLeft}
          onStayConnected={acknowledgeWarning}
          onDisconnect={voluntaryDisconnect}
        />
      )}
    </IdleTimeoutContext.Provider>
  );
}

export default IdleTimeoutWrapper;
